import React, { useState } from 'react';
import PatientControlButton from 'components/atoms/patentControlButton';
import ControlButtonDropdown from 'components/atoms/controlPanelDropdownButton/controlButtonDropdown';
import { useTranslation } from 'react-i18next';
import {
  useDoctorSendSms,
  useSendSms,
  useSmsButtons,
} from 'infrastructure/hooks/control-panel';
import { isRpmModule as isRpmModuleFunction } from 'infrastructure/functions';
import useUser from 'utils/useUser';
import SmsControlIcon from 'components/atoms/icons/smsControlIcon';
import SmsModal from 'components/templates/rpm/patients/patient/messages/modal';
import SmsDoctorModal from 'components/organisms/controlPanel/smsDoctorModal';
import BaseGrid from 'components/atoms/base-grid';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { smsTemplates } from 'infrastructure/consts';

import type { ModuleType } from 'infrastructure/enums';

const ToggleButton: React.FC<{ label: string; disabled?: boolean }> = (
  props,
) => {
  const { label, disabled } = props;
  return (
    <PatientControlButton
      label={label}
      Icon={SmsControlIcon}
      disabled={disabled}
    />
  );
};

interface IProps {
  module: ModuleType;
  patientGuid: string;
}

const SmsButtonsController: React.FC<IProps> = (props) => {
  const { module, patientGuid } = props;
  const { t } = useTranslation();

  const [currentModule, setCurrentModule] = useState<ModuleType | null>(null);
  const [smsPopupOpen, setSmsPopupOpen] = useState(false);
  const [doctorSmsPopupOpen, setDoctorSmsPopupOpen] = useState(false);

  const { userType, isMobileDevice } = useUser();
  const {
    doctorInfo,
    isBlockCcmModule,
    isBlockRpmModule,
    isCcmActionDisabled,
    isRpmModule,
    isCcmModule,
    isCombinedModule,
  } = useAccess({
    module,
    patientGuid,
  });

  const smsButtons = useSmsButtons({
    isAdminPanel: false,
    setSmsPopupOpen,
    userType,
    doctorWorkNumber: doctorInfo?.workNumber ?? undefined,
    setDoctorSmsPopupOpen,
    setCurrentModule,
  });

  const { sendSms, smsLoading } = useSendSms();
  const { sendDoctorSms, doctorSmsLoading } = useDoctorSendSms();

  const getLabel = (prefix: string) => {
    const label = t('labels.sms');
    if (isCombinedModule) return `${prefix} ${label}`;
    return label;
  };

  const columns = isMobileDevice || !isCombinedModule ? 1 : 2;

  return (
    <BaseGrid columns={columns}>
      {(isRpmModule || isCombinedModule) && (
        <ControlButtonDropdown
          toggleBtn={
            <ToggleButton
              label={getLabel(t('labels.rpm'))}
              disabled={isCcmActionDisabled || isBlockRpmModule}
            />
          }
          options={smsButtons}
          isRpm
        />
      )}

      {(isCcmModule || isCombinedModule) && (
        <ControlButtonDropdown
          toggleBtn={
            <ToggleButton
              label={getLabel(t('labels.ccm'))}
              disabled={isCcmActionDisabled || isBlockCcmModule}
            />
          }
          options={smsButtons}
          isRpm={false}
        />
      )}
      {smsPopupOpen && (
        <SmsModal
          isOpen={smsPopupOpen}
          toggle={() => {
            setSmsPopupOpen(false);
            setCurrentModule(null);
          }}
          patientGuid={patientGuid}
          smsTemplates={smsTemplates ?? []}
          loading={smsLoading}
          submit={sendSms}
          isRpmPanel={
            (currentModule && isRpmModuleFunction(currentModule)) || isRpmModule
          }
        />
      )}
      <SmsDoctorModal
        isOpen={doctorSmsPopupOpen}
        toggle={setDoctorSmsPopupOpen}
        patientGuid={patientGuid}
        loading={doctorSmsLoading}
        submit={sendDoctorSms}
      />
    </BaseGrid>
  );
};

export default SmsButtonsController;
